/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {getCVSPaginatedAssets} from '@ion/core/src/services/ArcGIS';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {AssetQueryParams} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime, removeQueries} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadAssetResults = UseDefaultQueryResult & {
  results: CVSAssetType[];
  refresh: Function;
  fetchNextPage: Function;
  hydrateData: Function;
  isKeyInCache: Function;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  reload: Function;
};

export interface UseLoadAsset {
  baseKey: QueryKey;
  enabled?: boolean;
  rowsPerPage?: number;
  includeSciDevice?: boolean;
  globalGroupId?: number;
  queryParams?: AssetQueryParams;
  staleTime?: number | DurationResult;
  enableAutoNextPageFetch?: boolean;
  // when mounting over stale cached pages, refetches only the first page
  // instead of every loaded page in sequence - later pages keep their cached
  // data. A first load, with nothing cached, fetches as usual ...
  refetchFirstPageOnMount?: boolean;
};

interface HydrateDataProp {
  data: CVSAssetType[],
  baseKey: QueryKey,
  globalGroupId: number,
  queryParams: AssetQueryParams
}

/**
 * The useLoadAssets hook provides the Query for referencing the current Assets data.
 *
 * ```typescript
 *
 * import {useLoadAssets} from '@ion/query';
 *
 *  ...
 *
 *  const {assets} = useLoadAssets();
 *
 * ```
 * 
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param   queryParams is built through Report filters to be used in the CVS request.
 * @param   globalGroupId used in the CVS request.
 * @returns UseLoadAssetResults containing the query results and the current Tracking Asset.
 */
export const useLoadAssets = ({
  baseKey,
  queryParams,
  globalGroupId,
  enabled = true,
  includeSciDevice = true,
  rowsPerPage = 10000,
  staleTime = getStaleTime(60, false, false),
  enableAutoNextPageFetch = true,
  refetchFirstPageOnMount = false
}: UseLoadAsset): UseLoadAssetResults => {
  const queryClient = useQueryClient();

  const keys = useMemo(() => (
    createKey(baseKey, globalGroupId, queryParams)
  ), [baseKey, globalGroupId, queryParams]);

  const fetchAssets = useCallback(async({pageParam = 0}) => {
    const {params} = queryParams || {};
    const startRow = pageParam ? ((pageParam - 1) * rowsPerPage) : 0;
    const results  = await getCVSPaginatedAssets({
      ...params,
      pageSize : rowsPerPage,
      startRow
    }, includeSciDevice);
    return results;
  }, [queryParams, rowsPerPage, includeSciDevice]);

  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchAssets,
    getNextPageParam : (lastPage, pages) => {
      if (lastPage?.length < rowsPerPage) {
        return undefined;
      }
      return pages.length + 1;
    },
    staleTime : staleTime as number,
    enabled,
    ...(refetchFirstPageOnMount ? {refetchOnMount: false} : {})
  } as any);

  useEffect(() => {
    if (refetchFirstPageOnMount && enabled && query.data && query.isStale) {
      query.refetch({refetchPage: (page, index) => index === 0} as any);
    }
  }, []);

  const hydrateData = ({data, baseKey, globalGroupId, queryParams}: HydrateDataProp) => {
    const keys = createKey(baseKey, globalGroupId, queryParams);
    queryClient.setQueryData(keys, (oldData: any) => ({
      pages : oldData?.pages?.map((page, index) => {
        const start = index * rowsPerPage;
        const end   = start + rowsPerPage;
        return data?.slice(start, end);
      }),
      pageParams : oldData?.pageParams
    }));
  };

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;

  const reload = useCallback(() => {
    queryClient.setQueryData(keys, {pages: [], pageParams: []});
    removeQueries(QueryKey.GET_CVS_ASSETS_COUNT, false);
    refetch();
  }, [keys, refetch]);

  const isKeyInCache = useCallback((key, globalGroupId, queryParams) => {
    const keys = createKey(key, globalGroupId, queryParams);
    return queryClient.getQueryData(keys) !== undefined;
  }, []);

  if (enabled && enableAutoNextPageFetch && hasNextPage && !isFetchingNextPage) {
    // Fetch the next page of data if there is a
    // next page and we are not currently fetching it
    fetchNextPage();
  }

  return createQueryResult(query, {
    results : data || [],
    refresh : refetch,
    isFetchingNextPage,
    fetchNextPage,
    isKeyInCache,
    hasNextPage,
    hydrateData,
    enabled,
    reload
  }) as UseLoadAssetResults;
};
